/**
 * The Workbench's recipe table.
 *
 * Phase 2c stripped the attachment machinery out of this file with the guns it belonged to: there
 * are no mods to fit, no rails to count and no weapon stats to recompute, so `MOD_EFFECTS`,
 * `slotUsage`, `fitErrors`, `slotCapacity` and `weaponWithMods` are gone rather than kept as
 * dead code with no callers.
 *
 * What is left is the job queue — which is good, tested and unrelated to guns — and four recipes
 * that only touch materials and consumables. The twelve gun and attachment recipes are **retired**
 * rather than flagged off one by one: they produced items that no longer exist, so a flag would
 * only have hidden a table that could not be crafted from. `CRAFTING_ENABLED` is the flag the
 * plan asked for, and it hides the whole bench until trade skills are designed.
 */
import { ITEMS } from './items.js';

/**
 * The Workbench is off in the slice (plan: "crafting and workbench screens hidden behind a flag,
 * not deleted"). The hub reads this to decide whether to draw the tab; the server still honours
 * the routes so an in-flight job finishes rather than vanishing.
 */
export const CRAFTING_ENABLED = false;

export interface Recipe {
  id: string;
  name: string;
  /** Hideout Workbench level required. */
  level: number;
  inputs: { itemId: string; qty: number }[];
  output: { itemId: string; qty: number };
  /** Wick fee. */
  fee: number;
}

export const RECIPES: Record<string, Recipe> = Object.fromEntries(
  (
    [
      {
        id: 'r_stim',
        name: 'Tallow Stim',
        level: 1,
        inputs: [
          { itemId: 'wire_bundle', qty: 1 },
          { itemId: 'ember_glass', qty: 1 },
        ],
        output: { itemId: 'med_stim', qty: 2 },
        fee: 10,
      },
      {
        id: 'r_kit',
        name: 'Warden Kit',
        level: 2,
        inputs: [
          { itemId: 'med_stim', qty: 2 },
          { itemId: 'wire_bundle', qty: 2 },
          { itemId: 'ember_glass', qty: 1 },
        ],
        output: { itemId: 'med_kit', qty: 1 },
        fee: 30,
      },
      {
        id: 'r_glass',
        name: 'Fused Glass',
        level: 1,
        inputs: [{ itemId: 'scrap_hull', qty: 10 }],
        output: { itemId: 'ember_glass', qty: 1 },
        fee: 15,
      },
      {
        id: 'r_shard',
        name: 'Shard Refinement',
        level: 3,
        inputs: [
          { itemId: 'colonist_tag', qty: 5 },
          { itemId: 'data_slate', qty: 1 },
        ],
        output: { itemId: 'hymn_shard', qty: 1 },
        fee: 80,
      },
    ] satisfies Recipe[]
  ).map((r) => [r.id, r]),
);

// ---------------------------------------------------------------- craft jobs
//
// A craft is a job the bench works on while you are elsewhere — the owner's ask, verbatim:
// "More Wick = more crafting time, less Wick = less crafting time."
//
//   seconds = clamp(CRAFT_MIN_SEC + fee ^ CRAFT_CURVE_EXP, CRAFT_MIN_SEC, CRAFT_MAX_SEC)
//
// The exponent is just over 1, so the 33x fee ladder (5 → 165 Wick) opens out rather than
// compressing: the shipped recipes run **13 s** (r_tin, fee 5) to **7 m 45 s** (r_drum, fee 165),
// with the middle of the ladder — Ember Sight and Wick Sidearm at 40 Wick — around 1 m 30 s. A
// sub-1 exponent was tried first and put the whole ladder inside a minute, which reads as a
// pause rather than a wait and makes both the 3-deep queue and the offline inbox note pointless.
// `CRAFT_MAX_SEC` (10 minutes) is the ceiling and a forward guard: a 260-Wick recipe would reach
// it, so anything dearer than the Tallow Drum lands there rather than running away.

export const CRAFT_MIN_SEC = 6;
export const CRAFT_CURVE_EXP = 1.2;
export const CRAFT_MAX_SEC = 600;
/** How many jobs one account may have on the bench at once. */
export const CRAFT_QUEUE_MAX = 3;
/** Cancelling returns the materials whole and half the Wick; the bench keeps the rest. */
export const CRAFT_CANCEL_REFUND = 0.5;

/** How long a job costing `wickCost` takes, in whole seconds. */
export function craftDurationSec(wickCost: number): number {
  if (!Number.isFinite(wickCost) || wickCost <= 0) return CRAFT_MIN_SEC;
  const raw = CRAFT_MIN_SEC + Math.pow(wickCost, CRAFT_CURVE_EXP);
  return Math.max(CRAFT_MIN_SEC, Math.min(CRAFT_MAX_SEC, Math.round(raw)));
}

/** Duration of a recipe's job. Unknown ids get the floor rather than a NaN. */
export function recipeDurationSec(recipeId: string): number {
  const recipe = RECIPES[recipeId];
  return recipe ? craftDurationSec(recipe.fee) : CRAFT_MIN_SEC;
}

/** Wick handed back when a job is cancelled. Rounds down: the bench keeps the odd Wick. */
export const craftCancelRefund = (fee: number): number =>
  Math.max(0, Math.floor(fee * CRAFT_CANCEL_REFUND));

/**
 * 0..1 through a job. Callers pass the server's own `startedAt`/`readyAt` (seconds) and their
 * best guess at now, so the bench animation and the server clock cannot drift apart by more
 * than the round trip.
 */
export function craftProgress(startedAtSec: number, readyAtSec: number, nowSec: number): number {
  const span = readyAtSec - startedAtSec;
  if (!(span > 0)) return 1;
  return Math.max(0, Math.min(1, (nowSec - startedAtSec) / span));
}

/** The five assembly stages the bench draws, keyed to progress. */
export const craftStage = (progress: number): 0 | 1 | 2 | 3 | 4 =>
  progress >= 1 ? 4 : progress >= 0.75 ? 3 : progress >= 0.5 ? 2 : progress >= 0.25 ? 1 : 0;

/** Sanity: every recipe references defined items. */
export function validateRecipes(): string[] {
  const bad: string[] = [];
  for (const r of Object.values(RECIPES)) {
    for (const i of r.inputs) if (!ITEMS[i.itemId]) bad.push(`${r.id}:${i.itemId}`);
    if (!ITEMS[r.output.itemId]) bad.push(`${r.id}:${r.output.itemId}`);
  }
  return bad;
}
